export interface LadderOrder {
  trade_order_id: number;
  quantity: number;
  trader_code: string;
  participant_type?: 'HUMAN' | 'VIRTUAL' | null;
  participant_display_name?: string | null;
  created_at: string;
}

export interface LadderLevel {
  price: number;
  total_quantity: number;
  orders: LadderOrder[];
}

export interface DepthOrderRow {
  key: string;
  price: number;
  quantity: number;
  trader_code: string;
  participant_type?: 'HUMAN' | 'VIRTUAL' | null;
  participant_display_name?: string | null;
  created_at: string;
}

export interface DepthRow {
  bid: DepthOrderRow | null;
  ask: DepthOrderRow | null;
}

export interface DepthBuildOptions {
  topBidOrderId?: number | null;
  topAskOrderId?: number | null;
}

export interface LadderAlignmentOptions {
  topBidOrderId?: number | null;
  topAskOrderId?: number | null;
  hasTopBid?: boolean;
  hasTopAsk?: boolean;
}

function getBestOrderId(levels: LadderLevel[]): number | null {
  for (const level of levels) {
    const firstOrder = (level.orders ?? [])[0];
    if (firstOrder?.trade_order_id != null) {
      return firstOrder.trade_order_id;
    }
  }

  return null;
}

function buildSideRows(levels: LadderLevel[], excludedOrderId?: number | null): DepthOrderRow[] {
  const rows: DepthOrderRow[] = [];

  levels.forEach((level) => {
    const remainingOrders = (level.orders ?? []).filter(
      (order) => order.trade_order_id !== excludedOrderId,
    );

    if (remainingOrders.length === 0) {
      return;
    }

    const groupedByTrader = remainingOrders.reduce<
      Array<{
        trader_code: string;
        participant_type?: 'HUMAN' | 'VIRTUAL' | null;
        participant_display_name?: string | null;
        quantity: number;
        created_at: string;
        order_ids: number[];
      }>
    >((groups, order) => {
      const existingGroup = groups.find((group) => group.trader_code === order.trader_code);

      if (existingGroup) {
        existingGroup.quantity += order.quantity;
        existingGroup.order_ids.push(order.trade_order_id);
        return groups;
      }

      groups.push({
        trader_code: order.trader_code,
        participant_type: order.participant_type,
        participant_display_name: order.participant_display_name,
        quantity: order.quantity,
        created_at: order.created_at,
        order_ids: [order.trade_order_id],
      });

      return groups;
    }, []);

    groupedByTrader.forEach((group) => {
      rows.push({
        key: `${level.price}-${group.order_ids.join('-')}`,
        price: level.price,
        quantity: group.quantity,
        trader_code: group.trader_code,
        participant_type: group.participant_type,
        participant_display_name: group.participant_display_name,
        created_at: group.created_at,
      });
    });
  });

  return rows;
}

export function isLadderAlignedWithTopOfBook(
  bids: LadderLevel[],
  asks: LadderLevel[],
  {
    topBidOrderId = null,
    topAskOrderId = null,
    hasTopBid = false,
    hasTopAsk = false,
  }: LadderAlignmentOptions = {},
) {
  const bestBidOrderId = getBestOrderId(bids);
  const bestAskOrderId = getBestOrderId(asks);

  if (hasTopBid) {
    if (topBidOrderId == null || bestBidOrderId !== topBidOrderId) {
      return false;
    }
  } else if (bestBidOrderId != null) {
    return false;
  }

  if (hasTopAsk) {
    if (topAskOrderId == null || bestAskOrderId !== topAskOrderId) {
      return false;
    }
  } else if (bestAskOrderId != null) {
    return false;
  }

  return true;
}

export function buildDepthRows(
  bids: LadderLevel[],
  asks: LadderLevel[],
  {
    topBidOrderId = null,
    topAskOrderId = null,
  }: DepthBuildOptions = {},
): DepthRow[] {
  const flattenedBids = buildSideRows(bids, topBidOrderId);
  const flattenedAsks = buildSideRows(asks, topAskOrderId);
  const maxDepth = Math.max(flattenedBids.length, flattenedAsks.length);

  return Array.from({ length: maxDepth }, (_, index) => ({
    bid: flattenedBids[index] ?? null,
    ask: flattenedAsks[index] ?? null,
  }));
}
